import { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { RegisterFormField } from "../../../../payment.types";
import AnotherFieldPopUp from "./AnotherFieldPopUp";
import AddCustomFieldPopUp from "./AddCustomFieldPopUp";

const labelTagStyle = {
  color: "#106048",
  fontFamily: "Montserrat",
  fontSize: "12px",
  fontWeight: 500,
  lineHeight: "18px",
  borderRadius: "16px",
  background: "#F3F7F6",
  padding: "2px 8px",
};

const FieldTag = ({ label }: { label: string }) => (
  <Typography component="span" sx={labelTagStyle}>
    {label}
  </Typography>
);

const RegisterFormFields = () => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext();
  const [isAnotherFieldPopUpOpen, setIsAnotherFieldPopUpOpen] = useState(false);
  const [isCustomFieldPopUpOpen, setIsCustomFieldPopUpOpen] = useState(false);

  const openAnotherFieldPopUp = () => setIsAnotherFieldPopUpOpen(true);
  const closeAnotherFieldPopUp = () => setIsAnotherFieldPopUpOpen(false);

  const openCustomFieldPopUp = () => setIsCustomFieldPopUpOpen(true);
  const closeCustomFieldPopUp = () => setIsCustomFieldPopUpOpen(false);

  const onAddingAnotherFields = (fields: RegisterFormField[]) => {
    const allFields = watch("registerFormFields");
    const defaultFields = allFields.filter((f: RegisterFormField) => f.default);
    const customFields = fields.filter((f: RegisterFormField) => !f.default && f.inserted);
    const cleanedCustomFields = customFields.filter(
      (newField) => !defaultFields.some((df: RegisterFormField) => df.title === newField.title)
    );
    const finalFields = [...defaultFields, ...cleanedCustomFields];
    setValue("registerFormFields", finalFields);
    closeAnotherFieldPopUp();
  };
  const onAddingCustomField = (field: RegisterFormField) => {
    const allFields = watch("registerFormFields");
    const finalFields = [...allFields, field];

    setValue("registerFormFields", finalFields);
    closeCustomFieldPopUp();
  };

  const registerFormFieldsList: RegisterFormField[] = watch("registerFormFields");

  return (
    <>
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", rowGap: "24px" }}>
        <Typography
          component="h6"
          sx={{
            color: "#344054",
            fontFamily: "Montserrat",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "20px",
            marginBottom: "16px",
          }}
        >
          {t("pages.payment.createPaymentLink.defaultInput")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            rowGap: "8px",
          }}
        >
          {registerFormFieldsList.map((field: RegisterFormField) => (
            <Box
              key={field.title}
              sx={{
                borderRadius: "12px",
                border: "1px solid #EAECF0",
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                padding: "16px",
              }}
            >
              <Typography
                component="span"
                sx={{
                  color: "#344054",
                  fontFamily: "Montserrat",
                  fontSize: "14px",
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: "20px",
                }}
              >
                {field.isCustom ? field.title : t(`pages.payment.createPaymentLink.${field.title}`)}
              </Typography>
              <Box sx={{ display: "flex", gap: "8px" }}>
                {field.default && <FieldTag label={t("pages.payment.createPaymentLink.default")} />}
                {field.required && <FieldTag label={t("pages.payment.createPaymentLink.required")} />}
              </Box>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: "12px", flexDirection: { xs: "column", md: "row" } }}>
          <Button
            variant="outlined-md"
            onClick={openAnotherFieldPopUp}
            sx={{ width: { xs: "100%", md: "fit-content" } }}
          >
            {t("pages.payment.createPaymentLink.addAnotherInput")}
          </Button>
          <Button
            variant="outlined-md"
            onClick={openCustomFieldPopUp}
            sx={{ width: { xs: "100%", md: "fit-content" } }}
          >
            {t("pages.payment.createPaymentLink.addCustomInputField")}
          </Button>
        </Box>
      </Box>
      <AnotherFieldPopUp
        open={isAnotherFieldPopUpOpen}
        onClose={closeAnotherFieldPopUp}
        onDone={onAddingAnotherFields}
      />
      <AddCustomFieldPopUp open={isCustomFieldPopUpOpen} onClose={closeCustomFieldPopUp} onDone={onAddingCustomField} />
    </>
  );
};

export default RegisterFormFields;
